/**
 * Prezzi di una riga di preventivo.
 *
 * Listino (`price`) e scontato (`priceDiscounted`) sono imponibili: l'IVA della
 * riga si aggiunge. Stesse funzioni, stessi nomi, in
 * barcio-api/src/utils/prezzoArticolo.js: l'API ricalcola il prezzo ivato,
 * qui serve a mostrarlo prima del salvataggio.
 */

const round2 = (n) => Math.round(n * 100) / 100;

const vuoto = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** Numero da un campo prezzo: null se vuoto, NaN se non numerico. */
export const numeroPrezzo = (v) => (vuoto(v) ? null : Number(v));

/**
 * Base imponibile della riga: lo scontato se c'e', altrimenti il listino.
 * Unico punto in cui si decide la precedenza (in attesa di conferma del cliente).
 */
export const prezzoBase = ({ listPrice, discountedPrice }) => {
  const scontato = numeroPrezzo(discountedPrice);
  return scontato !== null ? scontato : numeroPrezzo(listPrice) ?? 0;
};

/** Prezzo ivato: l'IVA si aggiunge alla base imponibile. */
export const prezzoIvato = (base, iva) =>
  round2(Number(base) * (1 + Number(iva) / 100));

export const MSG_SCONTATO =
  "Il prezzo scontato deve essere inferiore al prezzo di listino";

/**
 * Lo scontato e' facoltativo; se c'e' deve essere un numero >= 0 e minore del
 * listino. Restituisce il messaggio d'errore, oppure null.
 */
export const erroreScontato = (listPrice, discountedPrice) => {
  const scontato = numeroPrezzo(discountedPrice);
  if (scontato === null) return null;
  if (!Number.isFinite(scontato) || scontato < 0)
    return "Prezzo scontato non valido";
  const listino = numeroPrezzo(listPrice);
  if (listino !== null && Number.isFinite(listino) && scontato >= listino)
    return MSG_SCONTATO;
  return null;
};

/**
 * Prezzi di una riga copiata da un preventivo salvato dentro un preventivo
 * nuovo: listino e scontato dalla fotografia della riga, oppure dall'articolo
 * se la riga e' di prima dell'introduzione. Il prezzo ivato si ricalcola con
 * la regola nuova, come per ogni riga di un preventivo nuovo.
 */
export const prezziRigaDuplicata = (qi) => {
  const fotografata = qi?.listPrice != null;
  const listPrice = numeroPrezzo(fotografata ? qi.listPrice : qi?.item?.price);
  const discountedPrice = numeroPrezzo(
    fotografata ? qi.discountedPrice : qi?.item?.priceDiscounted
  );
  return {
    listPrice,
    discountedPrice,
    price: prezzoIvato(
      prezzoBase({ listPrice, discountedPrice }),
      qi?.iva ?? 22
    ),
  };
};

/**
 * Riga di un preventivo salvato riaperta in modifica. Il prezzo ivato resta
 * quello salvato: non si ricalcola, cosi' una riga non toccata non si sposta
 * di un centesimo. `regolaNuova` e' vero se la riga ha gia' i prezzi
 * fotografati: solo allora il salvataggio rimanda `discountedPrice`.
 */
export const prezziRigaSalvata = (qi) => {
  const fotografata = qi?.listPrice != null;
  return {
    price: Number(qi?.price ?? 0),
    listPrice: fotografata ? numeroPrezzo(qi.listPrice) : null,
    discountedPrice: fotografata ? numeroPrezzo(qi.discountedPrice) : null,
    regolaNuova: fotografata,
  };
};

/**
 * Anteprima della riga prima di aggiungerla: imponibile, aliquota, imposta e
 * totale per la quantita' fatturata (mq o ml per le categorie a scatola).
 * Il totale e' prezzo ivato x quantita', come lo calcola il preventivo;
 * l'imposta e' la differenza, cosi' le tre cifre tornano sempre.
 */
export const anteprimaRiga = ({
  listPrice,
  discountedPrice,
  iva,
  quantita,
}) => {
  const q = Number(quantita) || 0;
  const aliquota = Number(iva) || 0;
  const base = prezzoBase({ listPrice, discountedPrice });
  const imponibile = round2(base * q);
  const totale = round2(prezzoIvato(base, aliquota) * q);
  return {
    imponibile,
    aliquota,
    imposta: round2(totale - imponibile),
    totale,
  };
};
